/**
 * ✅ VÉRIFICATION - Système anti-faux profils
 * 
 * Méthodes de vérification :
 * 1. Email - Vérification par code envoyé par email
 * 2. Selfie Video - Fait un geste spécifique en caméra
 * 3. Badge IRL - Tu as participé à un événement (Speed Dating)
 * 
 * Badges obtenus :
 * - 📧 Email vérifié
 * - ✅ Vérifié (selfie)
 * - 🌟 IRL (participation événement)
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Camera, Video, Check, X, 
  RefreshCw, Sparkles, Star, Award,
  Phone, MapPin, Calendar, Mail, Lock
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { api } from '../../../../services/api';

// 🎯 Gestes de vérification possibles
const VERIFICATION_GESTURES = [
  { id: 'wave', label: 'Fais coucou de la main 👋', emoji: '👋' },
  { id: 'thumbs_up', label: 'Fais un pouce en l\'air 👍', emoji: '👍' },
  { id: 'peace', label: 'Fais le signe de la paix ✌️', emoji: '✌️' },
  { id: 'heart', label: 'Fais un cœur avec tes mains 💜', emoji: '💜' },
  { id: 'smile', label: 'Fais ton plus beau sourire 😁', emoji: '😁' }
];

// 🏅 Types de badges
export const VERIFICATION_BADGES = {
  email: {
    id: 'email',
    name: 'Email',
    emoji: '📧',
    color: 'cyan',
    description: 'Adresse email vérifiée'
  },
  verified: {
    id: 'verified',
    name: 'Vérifié',
    emoji: '✅',
    color: 'blue',
    description: 'Identité confirmée par selfie vidéo'
  },
  irl: {
    id: 'irl',
    name: 'IRL',
    emoji: '🌟',
    color: 'yellow',
    description: 'A participé à un événement en vrai'
  },
  phone: {
    id: 'phone',
    name: 'Téléphone',
    emoji: '📱',
    color: 'green',
    description: 'Numéro de téléphone vérifié'
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    emoji: '💎',
    color: 'purple',
    description: 'Membre premium Heartbeats'
  }
};

// 📧 Composant de vérification par EMAIL
export function EmailVerification({ customerId, onComplete, onClose, isDarkMode }) {
  const [step, setStep] = useState('email'); // email -> code -> success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  // Envoyer le code par email
  const sendCode = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Entre une adresse email valide');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/heartbeats/verify/email/send/${customerId}`, { email });
      
      if (response.success) {
        toast.success(`📧 Code envoyé à ${email}`);
        setStep('code');
        setCountdown(60); // 60 secondes avant de pouvoir renvoyer
        
        // En mode dev, afficher le code dans la console
        if (response.debug_code) {
          console.log('🔑 Code de vérification (dev):', response.debug_code);
        }
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };
  
  // Vérifier le code
  const verifyCode = async () => {
    if (code.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/heartbeats/verify/email/confirm/${customerId}`, { 
        email, 
        code 
      });
      
      if (response.success) {
        setStep('success');
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#06b6d4', '#22d3ee', '#67e8f9']
        });
        toast.success('📧 Email vérifié !');
      }
    } catch (error) {
      toast.error(error.message || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };
  
  // Countdown pour renvoyer le code
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className={`w-full max-w-md rounded-3xl overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-500" />
            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vérification Email
            </span>
          </div>
          <button onClick={onClose}>
            <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
        
        <div className="p-6">
          {/* Step: Email */}
          {step === 'email' && (
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center"
              >
                <Mail className="w-10 h-10 text-cyan-500" />
              </motion.div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vérifie ton email
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                On t'envoie un code à 6 chiffres pour confirmer ton email.
              </p>
              
              <Input
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : ''}`}
              />
              
              <Button
                size="lg"
                onClick={sendCode}
                disabled={loading || !email}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white w-full"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-5 h-5 mr-2" />
                )}
                Envoyer le code
              </Button>
            </div>
          )}
          
          {/* Step: Code */}
          {step === 'code' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center">
                <Lock className="w-10 h-10 text-cyan-500" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Entre le code
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Code envoyé à <strong>{email}</strong>
              </p>
              
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className={`mb-4 text-center text-2xl font-bold tracking-widest ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : ''}`}
              />
              
              <Button
                size="lg"
                onClick={verifyCode}
                disabled={loading || code.length !== 6}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white w-full mb-4"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Check className="w-5 h-5 mr-2" />
                )}
                Vérifier
              </Button>
              
              <button
                onClick={sendCode}
                disabled={countdown > 0 || loading}
                className={`text-sm ${countdown > 0 ? 'text-gray-400' : 'text-cyan-500 hover:text-cyan-400'}`}
              >
                {countdown > 0 ? `Renvoyer dans ${countdown}s` : 'Renvoyer le code'}
              </button>
            </div>
          )}
          
          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
                className="w-24 h-24 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center"
              >
                <Check className="w-12 h-12 text-cyan-500" />
              </motion.div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Email vérifié ! 📧
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Ton badge 📧 est maintenant visible sur ton profil.
              </p>
              
              <Button
                size="lg"
                onClick={() => {
                  onComplete && onComplete('email');
                  onClose();
                }}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white w-full"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Super, merci !
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// 📷 Composant de vérification par selfie vidéo
export function VideoVerification({ customerId, onComplete, onClose, isDarkMode }) {
  const [step, setStep] = useState('intro'); // intro -> camera -> gesture -> processing -> success
  const [gesture, setGesture] = useState(null);
  const [stream, setStream] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const videoRef = useRef(null);
  
  // Sélectionner un geste aléatoire
  useEffect(() => {
    const randomGesture = VERIFICATION_GESTURES[Math.floor(Math.random() * VERIFICATION_GESTURES.length)];
    setGesture(randomGesture);
  }, []);
  
  // Activer la caméra
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: false 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStep('gesture');
    } catch (error) {
      toast.error('Impossible d\'accéder à la caméra');
    }
  };
  
  // Lancer le compte à rebours et "capturer"
  const startCapture = () => {
    setStep('countdown');
    let count = 3;
    const interval = setInterval(() => {
      count--;
      setCountdown(count);
      if (count === 0) {
        clearInterval(interval);
        processVerification();
      }
    }, 1000);
  };
  
  // Traitement de vérification
  const processVerification = async () => {
    setStep('processing');
    
    // Arrêter la caméra
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    try {
      // Appel API backend pour valider la vérification
      const response = await api.post(`/heartbeats/verify/selfie/${customerId}`, {
        gestureCompleted: true
      });
      
      if (response.success) {
        setStep('success');
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#3B82F6', '#10B981', '#8B5CF6']
        });
        toast.success('✅ Profil vérifié avec succès !');
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
      setStep('intro');
    }
  };
  
  // Nettoyer la caméra à la fermeture
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className={`w-full max-w-md rounded-3xl overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vérification Selfie
            </span>
          </div>
          <button onClick={onClose}>
            <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
        
        <div className="p-6">
          {/* Intro */}
          {step === 'intro' && (
            <div className="text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center"
              >
                <Camera className="w-10 h-10 text-blue-500" />
              </motion.div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vérifie ton identité
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Prouve que c'est bien toi en faisant un selfie vidéo avec un geste spécifique.
              </p>
              
              <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  🏅 Avantages du badge Vérifié
                </h4>
                <ul className={`text-sm text-left space-y-1 ${isDarkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                  <li>• +50% de matchs en plus</li>
                  <li>• Badge ✅ visible sur ton profil</li>
                  <li>• Inspire confiance aux autres</li>
                  <li>• Accès aux profils vérifiés en priorité</li>
                </ul>
              </div>
              
              <Button
                size="lg"
                onClick={startCamera}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white w-full"
              >
                <Video className="w-5 h-5 mr-2" />
                Lancer la vérification
              </Button>
            </div>
          )}
          
          {/* Camera + Gesture */}
          {(step === 'gesture' || step === 'countdown') && (
            <div className="text-center">
              {/* Vidéo preview */}
              <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-black mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover transform scale-x-[-1]"
                />
                
                {/* Overlay avec countdown */}
                {step === 'countdown' && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <motion.span
                      key={countdown}
                      initial={{ scale: 2, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-6xl font-bold text-white"
                    >
                      {countdown}
                    </motion.span>
                  </div>
                )}
              </div>
              
              {/* Instruction geste */}
              <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                <p className={`text-lg font-bold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  {gesture?.emoji} {gesture?.label}
                </p>
              </div>
              
              {step === 'gesture' && (
                <Button
                  size="lg"
                  onClick={startCapture}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white w-full"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Je suis prêt(e) !
                </Button>
              )}
            </div>
          )}
          
          {/* Processing */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-200 border-t-blue-500"
              />
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Analyse en cours...
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Notre magie vérifie ton identité ✨
              </p>
            </div>
          )}
          
          {/* Success */}
          {step === 'success' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5 }}
                className="w-24 h-24 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center"
              >
                <Check className="w-12 h-12 text-green-500" />
              </motion.div>
              <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Tu es vérifié(e) ! 🎉
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Ton badge ✅ est maintenant visible sur ton profil.
              </p>
              
              <div className="flex items-center justify-center gap-2 mb-6">
                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Vérifié
                </span>
              </div>
              
              <Button
                size="lg"
                onClick={() => {
                  onComplete && onComplete('verified');
                  onClose();
                }}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white w-full"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Génial, merci !
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// 🏅 Affichage des badges sur un profil
export function VerificationBadges({ badges = [], size = 'md', showLabels = false }) {
  const sizeClasses = {
    sm: 'w-5 h-5 text-xs',
    md: 'w-6 h-6 text-sm',
    lg: 'w-8 h-8 text-base'
  };
  
  if (badges.length === 0) return null;
  
  return (
    <div className="flex items-center gap-1">
      {badges.map((badgeId) => {
        const badge = VERIFICATION_BADGES[badgeId];
        if (!badge) return null;
        
        return (
          <div
            key={badgeId}
            className={`${sizeClasses[size]} rounded-full flex items-center justify-center`}
            title={badge.description}
          >
            <span>{badge.emoji}</span>
            {showLabels && (
              <span className="ml-1 text-xs font-medium">{badge.name}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// 🎯 Modal principale de vérification - Choix entre Email et Selfie
export function VerificationHub({ customerId, onComplete, onClose, isDarkMode, currentBadges = [] }) {
  const [activeVerification, setActiveVerification] = useState(null); // null, 'email', 'selfie'
  
  const hasEmailBadge = currentBadges.includes('email');
  const hasVerifiedBadge = currentBadges.includes('verified');
  
  if (activeVerification === 'email') {
    return (
      <EmailVerification
        customerId={customerId}
        onComplete={(badge) => {
          setActiveVerification(null);
          onComplete && onComplete(badge);
        }}
        onClose={() => setActiveVerification(null)}
        isDarkMode={isDarkMode}
      />
    );
  }
  
  if (activeVerification === 'selfie') {
    return (
      <VideoVerification
        customerId={customerId}
        onComplete={(badge) => {
          setActiveVerification(null);
          onComplete && onComplete(badge);
        }}
        onClose={() => setActiveVerification(null)}
        isDarkMode={isDarkMode}
      />
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className={`w-full max-w-md rounded-3xl overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-pink-500" />
            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Obtiens tes badges ✨
            </span>
          </div>
          <button onClick={onClose}>
            <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
        
        <div className="p-6">
          <p className={`mb-6 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Les badges inspirent confiance et augmentent tes chances de match ! 💜
          </p>
          
          {/* Option Email */}
          <button
            onClick={() => !hasEmailBadge && setActiveVerification('email')}
            disabled={hasEmailBadge}
            className={`w-full p-4 rounded-2xl mb-3 text-left transition-all ${
              hasEmailBadge 
                ? 'opacity-50 cursor-default' 
                : 'hover:scale-[1.02] cursor-pointer'
            } ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                hasEmailBadge ? 'bg-green-500' : 'bg-gradient-to-br from-cyan-400 to-blue-500'
              }`}>
                {hasEmailBadge ? <Check className="w-6 h-6 text-white" /> : <Mail className="w-6 h-6 text-white" />}
              </div>
              <div className="flex-1">
                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  📧 Vérification Email
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {hasEmailBadge ? 'Déjà vérifié !' : 'Confirme ton adresse email'}
                </p>
              </div>
            </div>
          </button>
          
          {/* Option Selfie */}
          <button
            onClick={() => !hasVerifiedBadge && setActiveVerification('selfie')}
            disabled={hasVerifiedBadge}
            className={`w-full p-4 rounded-2xl mb-6 text-left transition-all ${
              hasVerifiedBadge 
                ? 'opacity-50 cursor-default' 
                : 'hover:scale-[1.02] cursor-pointer'
            } ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                hasVerifiedBadge ? 'bg-green-500' : 'bg-gradient-to-br from-blue-400 to-purple-500'
              }`}>
                {hasVerifiedBadge ? <Check className="w-6 h-6 text-white" /> : <Camera className="w-6 h-6 text-white" />}
              </div>
              <div className="flex-1">
                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  ✅ Vérification Selfie
                </h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {hasVerifiedBadge ? 'Déjà vérifié !' : 'Prouve que c\'est bien toi (+50% matchs)'}
                </p>
              </div>
            </div>
          </button>
          
          {/* Badges actuels */}
          {currentBadges.length > 0 && (
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Tes badges actuels :
              </p>
              <VerificationBadges badges={currentBadges} size="lg" showLabels />
            </div>
          )}
          
          {/* Tous les badges obtenus */}
          {hasEmailBadge && hasVerifiedBadge && (
            <div className={`mt-4 p-4 rounded-xl text-center ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
              <p className={`font-bold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                🎉 Tu as tous les badges disponibles !
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default VerificationHub;
